import { Sound } from "../audio/Sound.js";
import { VirtualInstrument } from "../audio/VirtualInstrument.js";
import { Colors } from "./Colors.js";
import { IData, VOConfig, VOMelody, VOMelodyInstrument, VOMelodyPair, VOMelodyPairItem, VOOrchestra, VOOrchestraTrack } from "./DataStructure.js";
import { Labels } from "./Labels.js";
import { ProgressList, ProgressListItem } from "./ProgressList.js";

export interface DataOptions {
    progressHandler?: {
        setProgress(loaded: number, total: number): void;
    };
}

export class Data {

    public data: IData;
    public config: VOConfig;
    public labels: Labels;
    public colors: Colors;

    private _options: DataOptions;

    constructor(options: DataOptions = {}) {
        this._options = e5.object.merge({

        }, options);
    }

    public async load(url: string, progress?: ProgressList): Promise<IData> {


        let progressItem: ProgressListItem = null;
        if (progress) {
            progressItem = progress.add(1);
        }

        const data: IData = await e5.load("json", url);

        let uid = 9999;

        const configTitle = e5.Url.params.get("config", "Default");
        this.config = data.phil_config?.find((v) => {
            return configTitle.toLocaleLowerCase() === v.title.toLocaleLowerCase();
        });
        if (!this.config) {
            let message = `Konfiguration mit dem Titel "${configTitle}" nicht vorhanden.`;
            if (data.phil_config.length > 0) {
                message += `<br><br>Folgende Konfiguration sind vorhanden:<br><div>${data.phil_config.map((c) => `<div><a href="${e5.Url.params.set("config", c.title)}">${c.title}</a></div>`).join("")}`;
            }
            console.warn(message);
            throw new Error(message);
        }

        this.colors = new Colors();

        const root = window.getComputedStyle(document.documentElement);
        for (let instrument of data.phil_orchestra_item) {
            instrument.color = root.getPropertyValue(instrument.color_name);
        }

        for (let mInstrument of data.phil_melody_item) {
            mInstrument.color = root.getPropertyValue(mInstrument.color_name);
        }

        for (let person of data.phil_intro_person) {
            // person.color = this.colors.fromName(person.color_name, "1");
            person.color = root.getPropertyValue(person.color_name);
        }

        for (let layout of data.phil_o_layout) {
            layout.kind = "orchestra";
            let index = 0;
            for (let place of layout.places) {
                place.index = index++;
            }
        }

        for (let layout of data.phil_intro_layout) {
            layout.kind = "introduction";
            let index = 0;
            for (let place of layout.places) {
                place.index = index++;
            }
        }

        for (let list of data.phil_intro_list) {
            if (!list.items) {
                list.items = [];
            }

            list.layout = e5.array.id(data.phil_intro_layout, list.layout as any);

            // console.log("PLACES", list.layout.places);

            let index = 0;
            for (let item of list.items) {
                item.person = e5.array.id(data.phil_intro_person, item.person as any);
                item.index = index++;
                item.place = e5.array.match(list.layout.places, "index", item.index);
                // console.log("PLACE", item.index, item.place);
            }
        }



        for (let pair of data.phil_melody_pair) {
            pair.color = root.getPropertyValue(pair.color_name);

            if (!pair.pairs) {
                pair.pairs = [];
            } else {
                const pairs: VOMelodyPairItem[] = [];
                for (let pairItem of pair.pairs) {
                    pairItem.instrument = e5.array.id(data.phil_melody_item, pairItem.instrument as any);
                    pairItem.melody = e5.array.id(data.phil_melody, pairItem.melody as any);
                    pairItem.id = uid++;

                    if (pairItem.instrument && pairItem.melody) {
                        pairs.push(pairItem);
                    }
                }
                pair.pairs = pairs;
            }
        }

        for (let config of data.phil_config) {
            config.person_list = e5.array.id(data.phil_intro_list, config.person_list as any);
            config.orchestra_list = e5.array.id(data.phil_orchestra_list, config.orchestra_list as any);
            config.melody_list = e5.array.id(data.phil_melody_list, config.melody_list as any);
            config.introduction_info = data.phil_info ? e5.array.id(data.phil_info, config.introduction_info as any) : null;
            config.melody_info = e5.array.id(data.phil_info, config.melody_info as any);
            config.orchestra_info = e5.array.id(data.phil_info, config.orchestra_info as any);
        }

        for (let orchestraList of data.phil_orchestra_list) {
            const orchestras: VOOrchestra[] = [];
            for (let id of orchestraList.orchestras) {
                const o = e5.array.id(data.phil_orchestra, id as any);
                if (o) {
                    orchestras.push(o);
                }
            }
            orchestraList.orchestras = orchestras;
        }

        for (let melodyList of data.phil_melody_list) {
            let pairs: VOMelodyPair[] = [];
            for (let item of melodyList.pairs) {

                let pair = e5.array.id(data.phil_melody_pair, item as any);
                if (pair) {
                    pairs.push(pair);
                } else {
                    console.log(item, data.phil_melody_pair);
                }
            }
            melodyList.pairs = pairs;
        }



        for (let orchestra of data.phil_orchestra) {
            orchestra.layout = e5.array.id(data.phil_o_layout, orchestra.layout as any);

            if (!orchestra.instruments) {
                orchestra.instruments = [];
                continue;
            }

            let instruments: VOOrchestraTrack[] = [];
            let position = 0;
            for (let orchestraInstrument of orchestra.instruments) {

                if (!orchestraInstrument.instrument) {
                    position++;
                    continue;
                }

                orchestraInstrument.index = position;
                orchestraInstrument.place = e5.array.match(orchestra.layout.places, "index", orchestraInstrument.index);
                position++;

                let inst = e5.array.id(data.phil_orchestra_item, orchestraInstrument.instrument as any);
                if (inst) {
                    orchestraInstrument.instrument = inst;
                    instruments.push(orchestraInstrument);
                } else {
                    console.warn("Could not find VOInstrument for VOOrchestra."
                        , "VOInstrument", orchestraInstrument.instrument
                        , "VOOrchestra", orchestra.title, orchestra.id);
                }
            }


            //FOR TEST
            // instruments.sort((a, b) => a.instrument.x - b.instrument.x);
            // instruments.forEach((a, index) => a.index = index);

            orchestra.instruments = instruments;
        }

        this.labels = new Labels({
            labels: data.phil_label
        });

        // console.log(this.config);

        this.data = data;

        if (progressItem) {
            progressItem.update(1, 1);
        }

        return data;
    }

    public async loadMelodyInstrumentsTones(audioContext: AudioContext, instruments: VOMelodyInstrument[], loadProgress: ProgressList, processingProgress: ProgressList): Promise<any> {

        var ps: Promise<any>[] = [];

        const loaded: number[] = [];
        for (var ins of instruments) {

            if (loaded.indexOf(ins.id) !== -1) {
                continue;
            }
            loaded.push(ins.id);

            for (var t of ins.tones) {

                const f = new Tone.Frequency(t.note);
                t.midi = f.toMidi();

                if (!t.sound) {
                    t.sound = new Sound({
                        context: audioContext
                    });
                    ps.push(t.sound.load(t.tone.url, ins.title, loadProgress.add(t.tone.filesize), processingProgress.add(1)));
                }
            }
        }

        return Promise.all(ps);
    }

    public async loadMelodyVirtualInstruments(items: VOMelodyInstrument[], loadProgress: ProgressList): Promise<any> {

        var ps: Promise<any>[] = [];

        for (var ins of items) {
            ins.virtual = new VirtualInstrument({
                data: ins
            });
            loadProgress.add(1).update(1, 1);
        }

        return Promise.all(ps);
    }

    public async loadMelodies(melodies: VOMelody[]): Promise<any> {

        var ps: Promise<any>[] = [];

        for (var mel of melodies) {
            if (mel.source === "midi") {
                var midiFile = await MidiLoader.fromUrl(mel.midi.url);

                let track: any = null;
                for (var t of midiFile.tracks) {
                    if (t.notes && t.notes.length > 0) {
                        track = t;
                        break;
                    }
                }

                if (!track) {
                    console.log(midiFile);
                    throw new Error(`Cannot find track with notes of Midi file "${mel.title}" ID:${mel.id}`);
                }

                var midi: any = track.notes;
                if (!track.notes[0]) {
                    console.log(midiFile);
                    throw new Error(`Cannot read notes of Midi file "${mel.title}" ID:${mel.id}`);
                }

                var offset = 0;//track.notes[0].time;
                mel.duration = track.duration - offset;
                midi.map((v: any, index: any) => {
                    v.dur = v.duration;
                    v.time -= offset;
                    v.index = index;
                    return v;
                });
                mel.notes = midi;
            } else if (mel.source === "notes") {

                if (mel.timing === "fixed") {
                    var time = 0;
                    for (var i = 0; i < mel.notes.length; ++i) {
                        mel.notes[i].time = time;
                        time += mel.timing_value;
                    }
                } else {
                    //Relative time to absolute time
                    let acc = 0;
                    for (var i = 0; i < mel.notes.length; ++i) {
                        acc += mel.notes[i].time;
                        mel.notes[i].time = acc;
                    }
                }

                if (mel.velocity === "fixed") {
                    for (var i = 0; i < mel.notes.length; ++i) {
                        mel.notes[i].velocity = mel.velocity_value;
                    }
                }

                for (var i = 0; i < mel.notes.length; ++i) {
                    var t = new Tone.Frequency(mel.notes[i].name);
                    mel.notes[i].midi = t.toMidi();
                    if (!mel.notes[i].velocity) {
                        mel.notes[i].velocity = 0.6;
                    }
                    mel.notes[i].index = i;
                }
            } else {
                console.warn("Unknown source type for melody", mel.source);
            }

            var maxNotes = 0;
            for (var part of mel.parts) {
                maxNotes += part.count;
            }
            if (maxNotes > mel.notes.length) {
                throw new Error(`The Melody "${mel.title}" has only ${mel.notes.length} notes but the sum of the parts are ${maxNotes} notes long.`);
            }
            mel.notes = mel.notes.slice(0, maxNotes);

            var sorted = mel.notes.slice().sort((a, b) => (a.time + a.dur) - (b.time + b.dur));
            var last = sorted[sorted.length - 1];
            mel.duration = last.time + last.dur;

            var startIndex = 0;
            var index = 0;
            var countTotal = 0;
            for (var part of mel.parts) {
                // part.notes = mel.notes.slice(startIndex, startIndex + part.count);
                part.notes = mel.notes.slice(0, startIndex + part.count);
                part.index = index;
                countTotal += part.count;
                part.countTotal = countTotal;
                index++;

                var off = part.notes[0].time;
                part.notes = part.notes.map((n) => {
                    return {
                        dur: n.dur,
                        name: n.name,
                        time: n.time - off,
                        index: n.index,
                        midi: n.midi,
                        velocity: n.velocity
                    }
                });
                startIndex += part.count;
            }

            // console.log(JSON.stringify(mel.notes.map((t) => {
            //     return {
            //         name: t.name,
            //         time: parseFloat(t.time.toFixed(4)),
            //         dur: parseFloat(t.dur.toFixed(4)),
            //         velocity: parseFloat(t.velocity.toFixed(4)),
            //     }
            // })));
        }

        // const scale = 0.5;
        // for (var mel of melodies) {
        //     mel.duration *= scale;
        //     for (var n of mel.notes) {
        //         n.time *= scale;
        //     }
        // }

        return Promise.all(ps);
    }
}